import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";
import "leaflet-control-geocoder";
import logo from "../assets/image/eblackdog_sentinal.png";

const initialForm = {
  run_id: "",
  member_id: "",
  title: "",
  vehicle: "",
  start_time: "",
  end_time: "",
  from_name: "",
  from_lat: "",
  from_lng: "",
  to_name: "",
  to_lat: "",
  to_lng: "",
};

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function valueOrEmpty(value) {
  return value === null || value === undefined ? "" : String(value);
}

function api(path) {
  const base = API_BASE_URL.replace(/\/$/, "");
  if (base.endsWith("/api") && path.startsWith("/api/")) {
    return `${base}${path.replace(/^\/api/, "")}`;
  }
  return `${base}${path}`;
}

function toDatetimeLocal(value) {
  if (!value) return "";

  const raw = String(value).trim();
  if (!raw) return "";

  const normalized = raw.replace("T", " ").split(".")[0];
  const [datePart, timePart = "00:00:00"] = normalized.split(" ");

  const [yyyy, mm, dd] = datePart.split("-").map(Number);
  const [hh = 0, mi = 0] = timePart.split(":").map(Number);

  if (!yyyy || !mm || !dd) return "";

  const pad = (n) => String(n).padStart(2, "0");

  return `${yyyy}-${pad(mm)}-${pad(dd)}T${pad(hh)}:${pad(mi)}`;
}

function createCustomIcon({ label, bg = "#2563eb", border = "#ffffff", textColor = "#ffffff", isLogo = false, logoUrl = "" }) {
  if (isLogo && logoUrl) {
    return L.divIcon({
      className: "custom-map-pin-wrapper",
      html: `
        <div style="
          position: relative;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: ${bg};
          border: 3px solid ${border};
          box-shadow: 0 4px 14px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        ">
          <img src="${logoUrl}" alt="logo" style="width: 28px; height: 28px; object-fit: contain;" />
        </div>
      `,
      iconSize: [40, 40],
      iconAnchor: [20, 40],
      popupAnchor: [0, -32],
    });
  }

  return L.divIcon({
    className: "custom-map-pin-wrapper",
    html: `
      <div style="
        position: relative;
        width: 30px;
        height: 30px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        background: ${bg};
        border: 2px solid ${border};
        box-shadow: 0 4px 10px rgba(0,0,0,0.25);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <span style="
          transform: rotate(45deg);
          font-size: 12px;
          font-weight: 700;
          color: ${textColor};
          font-family: Arial, sans-serif;
          line-height: 1;
        ">
          ${label}
        </span>
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -28],
  });
}

const fromIcon = createCustomIcon({ label: "F", bg: "#16a34a", isLogo: true, logoUrl: logo });
const toIcon = createCustomIcon({ label: "T", bg: "#dc2626", isLogo: true, logoUrl: logo });

export default function AddEscort() {
  const navigate = useNavigate();
  const location = useLocation();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const editId = params.get("edit_id") || "";
  const isEdit = Boolean(editId);

  const [form, setForm] = useState(initialForm);
  const [guards, setGuards] = useState([]);

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [activeTarget, setActiveTarget] = useState(null);
  const activeTargetRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const geocoderRef = useRef(null);
  const fromMarkerRef = useRef(null);
  const toMarkerRef = useRef(null);
  const didFitBoundsRef = useRef(false);

  const setTarget = (target) => {
    setActiveTarget(target);
    activeTargetRef.current = target;
    toast(`Click on the map or use search to set: ${target.toUpperCase()} location`, {
      icon: "📍",
      duration: 3000,
    });
  };

  const setLatLngOnTarget = useCallback((target, lat, lng, shouldCenter = false, label = "") => {
    if (!target) return;

    const latNum = Number(lat);
    const lngNum = Number(lng);

    if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return;

    const lat7 = latNum.toFixed(7);
    const lng7 = lngNum.toFixed(7);

    if (target === "from") {
      setForm((prev) => ({
        ...prev,
        from_lat: lat7,
        from_lng: lng7,
        from_name: label || prev.from_name,
      }));
    }

    if (target === "to") {
      setForm((prev) => ({
        ...prev,
        to_lat: lat7,
        to_lng: lng7,
        to_name: label || prev.to_name,
      }));
    }

    if (shouldCenter && mapInstanceRef.current) {
      mapInstanceRef.current.invalidateSize(true);
      mapInstanceRef.current.setView([latNum, lngNum], 16);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    const isLoggedIn = localStorage.getItem("isLoggedIn");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    fetchPageData(token);
  }, [navigate, editId]);

  useEffect(() => {
    if (loading) return;
    if (!mapRef.current || mapInstanceRef.current) return;

    // Fix for leaflet icon issue
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
      iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
      shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    });

    const map = L.map(mapRef.current, {
      zoomControl: true,
      attributionControl: true,
    });

    mapInstanceRef.current = map;

    map.setView([20.5937, 78.9629], 5);

    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap contributors",
    }).addTo(map);

    // Fix: Create geocoder with proper options and add to map
    const geocoder = L.Control.geocoder({
      defaultMarkGeocode: false,
      position: 'topleft',
      placeholder: 'Search location...',
      errorMessage: 'Location not found',
    })
    .on("markgeocode", (e) => {
      const target = activeTargetRef.current;

      if (!target) {
        toast.error("Please click Pick on Map for From or To first.");
        return;
      }

      const center = e.geocode.center;

      if (e.geocode.bbox) {
        map.fitBounds(e.geocode.bbox);
      } else {
        map.setView(center, 16);
      }

      setLatLngOnTarget(target, center.lat, center.lng, true, e.geocode.name || "");
      toast.success(`Location "${e.geocode.name}" found and pinned!`);
    })
    .addTo(map);

    geocoderRef.current = geocoder;

    map.on("click", (ev) => {
      const target = activeTargetRef.current;

      if (!target) {
        toast.error("Please click Pick on Map for From or To first.");
        return;
      }

      setLatLngOnTarget(target, ev.latlng.lat, ev.latlng.lng, true);
    });

    const resizeMap = () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.invalidateSize(true);
      }
    };

    setTimeout(resizeMap, 100);
    setTimeout(resizeMap, 500);
    setTimeout(resizeMap, 1000);
    setTimeout(resizeMap, 1800);

    window.addEventListener("resize", resizeMap);

    return () => {
      window.removeEventListener("resize", resizeMap);
      map.remove();
      mapInstanceRef.current = null;
      geocoderRef.current = null;
      fromMarkerRef.current = null;
      toMarkerRef.current = null;
      didFitBoundsRef.current = false;
    };
  }, [loading, setLatLngOnTarget]);

  useEffect(() => {
    syncMarkers();
  }, [form.from_lat, form.from_lng, form.to_lat, form.to_lng, form.from_name, form.to_name]);

  const fetchPageData = async (token) => {
    try {
      setLoading(true);

      const requests = [
        fetch(api("/api/dashboard/admin/dashboard"), {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(api("/api/escort/guards"), {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ];

      if (editId) {
        requests.push(
          fetch(api(`/api/escort/runs/${editId}`), {
            headers: { Authorization: `Bearer ${token}` },
          })
        );
      }

      const [dashboardRes, guardsRes, escortRes] = await Promise.all(requests);

      if (dashboardRes.ok) {
        const dashboardJson = await dashboardRes.json();
        const companyDetails = dashboardJson?.data?.companyDetails || {};
        setDashboardData((prev) => ({ ...prev, ...companyDetails }));
      }

      if (guardsRes.ok) {
        const guardsJson = await guardsRes.json();
        setGuards(guardsJson?.data?.guards || guardsJson?.data || []);
      }

      if (editId) {
        if (!escortRes?.ok) throw new Error("Failed to fetch escort run details");

        const escortJson = await escortRes.json();
        const run = escortJson?.data?.run || escortJson?.data || {};

        setForm({
          run_id: valueOrEmpty(run.run_id || run.id || editId),
          member_id: valueOrEmpty(run.member_id),
          title: valueOrEmpty(run.title),
          vehicle: valueOrEmpty(run.vehicle),
          start_time: toDatetimeLocal(run.start_time),
          end_time: toDatetimeLocal(run.end_time),
          from_name: valueOrEmpty(run.from_name),
          from_lat: valueOrEmpty(run.start_lat || run.from_lat),
          from_lng: valueOrEmpty(run.start_lng || run.from_lng),
          to_name: valueOrEmpty(run.to_name),
          to_lat: valueOrEmpty(run.end_lat || run.to_lat),
          to_lng: valueOrEmpty(run.end_lng || run.to_lng),
        });
      }
    } catch (error) {
      console.error("Add escort page error:", error);
      toast.error(error.message || "Failed to load escort page.");
    } finally {
      setLoading(false);
    }
  };

  const syncMarkers = () => {
    const map = mapInstanceRef.current;
    if (!map) return;

    map.invalidateSize(true);

    const fLat = parseFloat(form.from_lat || "");
    const fLng = parseFloat(form.from_lng || "");
    const tLat = parseFloat(form.to_lat || "");
    const tLng = parseFloat(form.to_lng || "");

    if (Number.isFinite(fLat) && Number.isFinite(fLng)) {
      if (fromMarkerRef.current) map.removeLayer(fromMarkerRef.current);

      fromMarkerRef.current = L.marker([fLat, fLng], { icon: fromIcon })
        .addTo(map)
        .bindPopup(`From: ${form.from_name || "Selected location"}`);
    } else if (fromMarkerRef.current) {
      map.removeLayer(fromMarkerRef.current);
      fromMarkerRef.current = null;
    }

    if (Number.isFinite(tLat) && Number.isFinite(tLng)) {
      if (toMarkerRef.current) map.removeLayer(toMarkerRef.current);

      toMarkerRef.current = L.marker([tLat, tLng], { icon: toIcon })
        .addTo(map)
        .bindPopup(`To: ${form.to_name || "Selected location"}`);
    } else if (toMarkerRef.current) {
      map.removeLayer(toMarkerRef.current);
      toMarkerRef.current = null;
    }

    const layers = [fromMarkerRef.current, toMarkerRef.current].filter(Boolean);

    if (layers.length > 0) {
      const group = L.featureGroup(layers);
      map.fitBounds(group.getBounds().pad(0.25));
      didFitBoundsRef.current = true;
    } else if (!didFitBoundsRef.current) {
      map.setView([20.5937, 78.9629], 5);
    }
  };

  const geocodeAddress = (target, text) => {
    const query = String(text || "").trim();

    if (!query) {
      toast.error("Enter a location name first.");
      return;
    }

    setActiveTarget(target);
    activeTargetRef.current = target;

    fetch(
      `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(
        query
      )}`
    )
      .then((res) => res.json())
      .then((results) => {
        if (!Array.isArray(results) || !results[0]) {
          toast.error("Location not found.");
          return;
        }

        const item = results[0];
        const lat = Number(item.lat);
        const lng = Number(item.lon);

        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          toast.error("Invalid location coordinates.");
          return;
        }

        const label = item.display_name || query;

        setLatLngOnTarget(target, lat, lng, true, label);

        if (mapInstanceRef.current) {
          mapInstanceRef.current.setView([lat, lng], 16);
        }

        toast.success(`Location found and pinned!`);
      })
      .catch((error) => {
        console.error("Search location error:", error);
        toast.error("Search location failed.");
      });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!form.member_id.trim()) return "Guard is required.";
    if (!form.title.trim()) return "Title is required.";
    if (!form.from_name.trim()) return "From location is required.";
    if (!form.to_name.trim()) return "To location is required.";
    if (!form.from_lat || !form.from_lng) return "Please set FROM location on map.";
    if (!form.to_lat || !form.to_lng) return "Please set TO location on map.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();

    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      setSaving(true);

      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const payload = {
        member_id: form.member_id.trim(),
        title: form.title.trim(),
        vehicle: form.vehicle.trim(),
        start_time: form.start_time,
        end_time: form.end_time,
        from_name: form.from_name.trim(),
        from_lat: form.from_lat,
        from_lng: form.from_lng,
        to_name: form.to_name.trim(),
        to_lat: form.to_lat,
        to_lng: form.to_lng,
      };

      const url = isEdit ? api(`/api/escort/runs/${form.run_id || editId}`) : api("/api/escort/runs");

      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to save escort run");
      }

      if (isEdit) {
        toast.success("Escort run updated successfully!");
      } else {
        toast.success("Escort run created successfully!");
        setForm(initialForm);
        navigate("/escort_list", { replace: true });
      }
    } catch (error) {
      console.error("Save escort error:", error);
      toast.error(error.message || "Failed to save escort run.");
    } finally {
      setSaving(false);
    }
  };

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/create_route" : "/create_route"),
    [dashboardData.user_map]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading escort management...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="escort"
      activeMenu="escort"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "white",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "white",
            },
          },
        }}
      />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
          Escort Run
        </h1>
        <p className="text-[13px] text-[var(--text-muted)] m-0 mb-[22px]">
          {isEdit ? "Edit an existing escort run" : "Create a new escort run"}
        </p>

        {/* Top Card */}
        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />
          <div className="flex items-center justify-between gap-3.5 flex-wrap">
            <div>
              <h2 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                {isEdit ? "Edit Escort" : "New Escort"}
              </h2>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Manage guard, title, vehicle, planned time, and route points
              </p>
            </div>
            <div className="flex gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => navigate(routePage)}
                className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
                style={{
                  background: "var(--btn-bg)",
                  color: "var(--btn-text)",
                  boxShadow: "0 4px 18px var(--accent-glow)",
                }}
              >
                Normal Route
              </button>
              <button
                type="button"
                onClick={() => navigate("/escort_list")}
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5"
              >
                Escort List
              </button>
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="mb-3.5">
            <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
              {isEdit ? "Edit Escort Run" : "New Escort Run"}
            </h3>
            <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
              Create escort run and pick from/to coordinates directly from the map
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Guard <span className="text-[var(--red)] ml-0.5">*</span>
                </label>
                <select
                  name="member_id"
                  value={form.member_id}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                >
                  <option value="">— Select Guard —</option>
                  {guards.map((guard) => {
                    const memberId =
                      guard.member_id || guard.fld_memberId || guard.fld_memberld || guard.memberId;
                    const label = guard.label || guard.fld_name || guard.fld_username || memberId;

                    return (
                      <option key={memberId} value={memberId}>
                        {label} ({memberId})
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Title <span className="text-[var(--red)] ml-0.5">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  placeholder="e.g., Coimbatore → Erode"
                  required
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Vehicle No.
                </label>
                <input
                  type="text"
                  name="vehicle"
                  value={form.vehicle}
                  onChange={handleChange}
                  placeholder="e.g., TN 66 AB 1234"
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                />
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Planned Start / End
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="datetime-local"
                    name="start_time"
                    value={form.start_time}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                  />
                  <input
                    type="datetime-local"
                    name="end_time"
                    value={form.end_time}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  From <span className="text-[var(--red)] ml-0.5">*</span>
                </label>
                <div className="space-y-3">
                  <input
                    type="text"
                    name="from_name"
                    value={form.from_name}
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        geocodeAddress("from", form.from_name);
                      }
                    }}
                    placeholder="Search or click on map"
                    required
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={form.from_lat}
                      readOnly
                      placeholder="Lat"
                      className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                    />
                    <input
                      type="text"
                      value={form.from_lng}
                      readOnly
                      placeholder="Lng"
                      className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                    />
                  </div>

                  <div className="flex gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={() => setTarget("from")}
                      className="border-0 rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90"
                      style={{
                        background: "var(--btn-bg)",
                        color: "var(--btn-text)",
                      }}
                    >
                      Pick on Map
                    </button>
                    <button
                      type="button"
                      onClick={() => geocodeAddress("from", form.from_name)}
                      className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)]"
                    >
                      Search Location
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  To <span className="text-[var(--red)] ml-0.5">*</span>
                </label>
                <div className="space-y-3">
                  <input
                    type="text"
                    name="to_name"
                    value={form.to_name}
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        geocodeAddress("to", form.to_name);
                      }
                    }}
                    placeholder="Search or click on map"
                    required
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={form.to_lat}
                      readOnly
                      placeholder="Lat"
                      className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                    />
                    <input
                      type="text"
                      value={form.to_lng}
                      readOnly
                      placeholder="Lng"
                      className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                    />
                  </div>

                  <div className="flex gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={() => setTarget("to")}
                      className="border-0 rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90"
                      style={{
                        background: "var(--btn-bg)",
                        color: "var(--btn-text)",
                      }}
                    >
                      Pick on Map
                    </button>
                    <button
                      type="button"
                      onClick={() => geocodeAddress("to", form.to_name)}
                      className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)]"
                    >
                      Search Location
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-5">
              <button
                type="submit"
                disabled={saving}
                className="w-full border-0 rounded-xl px-5 py-3 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                style={{
                  background: "var(--btn-bg)",
                  color: "var(--btn-text)",
                  boxShadow: "0 4px 18px var(--accent-glow)",
                }}
              >
                {saving ? "Saving..." : isEdit ? "Update Escort Run" : "Save Escort Run"}
              </button>
            </div>
          </form>
        </div>

        {/* Map Section */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <div className="mb-3.5">
            <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
              Escort Map
            </h3>
            <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
              Active picker: {activeTarget ? activeTarget.toUpperCase() : "None"}
            </p>
          </div>

          <div className="w-full h-[520px] min-h-[520px] relative overflow-hidden rounded-2xl bg-[var(--surface)] border border-[var(--surface-border)]">
            <div
              ref={mapRef}
              id="escort-leaflet-map"
              className="w-full h-[520px] min-h-[520px] block relative z-[1] rounded-2xl overflow-hidden bg-[var(--surface)]"
            />
          </div>
        </div>
      </div>

      <style>{`
        #escort-leaflet-map .leaflet-container,
        .leaflet-container {
          width: 100% !important;
          height: 520px !important;
          min-height: 520px !important;
          display: block !important;
          background: var(--surface) !important;
          z-index: 1 !important;
        }

        .leaflet-pane {
          z-index: 2 !important;
        }

        .leaflet-top,
        .leaflet-bottom {
          z-index: 10 !important;
        }

        .leaflet-control-container {
          z-index: 20 !important;
        }

        .custom-map-pin-wrapper {
          background: transparent !important;
          border: none !important;
        }

        .leaflet-control-geocoder {
          border-radius: 12px !important;
          border: 1px solid var(--surface-border) !important;
          background: var(--card-bg) !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
          display: flex !important;
          align-items: center !important;
          padding: 4px !important;
          margin: 10px !important;
        }

        .leaflet-control-geocoder-form {
          display: flex !important;
          align-items: center !important;
        }

        .leaflet-control-geocoder input {
          background: var(--surface) !important;
          color: var(--text-primary) !important;
          padding: 8px 12px !important;
          border-radius: 8px !important;
          border: none !important;
          outline: none !important;
          font-size: 13px !important;
          font-family: 'Inter', system-ui, sans-serif !important;
          min-width: 200px !important;
        }

        .leaflet-control-geocoder input::placeholder {
          color: var(--text-faint) !important;
        }

        .leaflet-control-geocoder .leaflet-control-geocoder-icon {
          background: var(--btn-bg) !important;
          border-radius: 8px !important;
          width: 32px !important;
          height: 32px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          cursor: pointer !important;
          margin-left: 4px !important;
          border: none !important;
        }

        .leaflet-control-geocoder .leaflet-control-geocoder-icon:before {
          content: "🔍" !important;
          font-size: 14px !important;
          line-height: 1 !important;
        }

        .leaflet-popup-content-wrapper {
          background: var(--card-bg) !important;
          color: var(--text-primary) !important;
          border-radius: 12px !important;
          border: 1px solid var(--card-border) !important;
        }

        .leaflet-popup-tip {
          background: var(--card-bg) !important;
        }

        .leaflet-popup-content {
          color: var(--text-primary) !important;
          font-family: 'Inter', system-ui, sans-serif !important;
          font-size: 13px !important;
          font-weight: 600 !important;
        }

        .leaflet-control-zoom {
          margin: 10px !important;
        }

        .leaflet-control-zoom a {
          background: var(--card-bg) !important;
          color: var(--text-primary) !important;
          border-color: var(--surface-border) !important;
        }

        .leaflet-control-zoom a:hover {
          background: var(--accent-faint) !important;
        }
      `}</style>
    </Layout>
  );
}